import {Button} from '@components/ui/button'
import {
    Globe02Icon,
    LinkExternal01Icon,
    LogOut01Icon,
    Menu01Icon,
    Settings01Icon,
    XCloseIcon
} from '@components/ui/icon'
import {FC, useRef, useState} from 'react'
import {UserCard} from '@components/commons/user-card'
import {User} from '@features/user/types.ts'
import {useTranslation, APP_LANGUAGES} from '@/translations/i18n.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {PHOTOGRAPHER_RESOURCE_CENTER_URL} from '@features/insights/constants'
import {LangSelectionModal} from '@components/commons/lang-selection-modal'
import {Spinner} from '@components/ui/spinner'
import {useLogout} from '@features/user/queries/useLogout.ts'
import {Badge} from '@components/ui/badge'
import {NavLink} from 'react-router-dom'
import {cn} from '@utilities/helpers.ts'

const menuItemClassName =
    'flex items-center gap-2 rounded-lg px-2.5 py-3 text-sm font-medium transition-colors hover:bg-neutral-100'
const menuItemNavLinkClassName = cn(menuItemClassName, '[&.active]:bg-neutral-100')
const disabledMenuItemClassName = 'opacity-50 cursor-not-allowed hover:bg-white'
const menuGroupClassName = 'p-1 px-2 not-last:border-b not-last:border-neutral-200'

export const MobileMenu: FC<{user: User; adminAccessToken?: string | null}> = ({user, adminAccessToken}) => {
    const {
        t,
        i18n: {language}
    } = useTranslation()
    const menuRef = useRef<HTMLDivElement>(null)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)
    const logoutMutation = useLogout()

    return (
        <>
            <Button shape="square" variant="ghost" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <XCloseIcon /> : <Menu01Icon />}
            </Button>
            {isMenuOpen && <div className="fixed top-16 left-0 z-mobile-menu h-full w-full bg-black/10" />}
            <div
                className="fixed top-16 left-0 z-mobile-menu w-full overflow-y-hidden bg-white shadow-md transition-all duration-400"
                style={{height: isMenuOpen ? menuRef.current?.scrollHeight : 0}}
                ref={menuRef}
            >
                <UserCard user={user} />
                <div className={menuGroupClassName}>
                    <NavLink
                        to={routes.HOME.handle.fullPath}
                        className={menuItemNavLinkClassName}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        {t('home:title')}
                    </NavLink>
                    <NavLink
                        to={routes.PAYMENTS.handle.fullPath}
                        className={menuItemNavLinkClassName}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        {t('admin:payments:title')}
                    </NavLink>
                    {/* TODO: remove Badge and disabled state when we have the feature @InsightsPage */}
                    {adminAccessToken || user.isInsightsVisible ? (
                        <NavLink
                            to={routes.INSIGHTS.handle.fullPath}
                            className={menuItemNavLinkClassName}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            {t('insights:title')}
                        </NavLink>
                    ) : (
                        <div className="flex w-full items-center gap-1">
                            <div className={cn(menuItemNavLinkClassName, disabledMenuItemClassName)}>
                                {t('insights:title')}
                            </div>

                            <Badge variant="blue">
                                {t('settings:personal_information:coming_soon_calendar_text_small')}
                            </Badge>
                        </div>
                    )}
                </div>
                <div className={menuGroupClassName}>
                    {(!user.isInspector || user.isCreator) && (
                        <a
                            href={PHOTOGRAPHER_RESOURCE_CENTER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={menuItemClassName}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <LinkExternal01Icon size={20} />
                            {t('menu:photographer_resource_center')}
                        </a>
                    )}
                    <NavLink
                        to={routes.SETTINGS.handle.fullPath}
                        className={menuItemNavLinkClassName}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <Settings01Icon size={20} />
                        {t('menu:settings')}
                    </NavLink>
                    <div className={menuItemClassName} onClick={() => setIsLanguageModalOpen(true)}>
                        <Globe02Icon size={20} />
                        {APP_LANGUAGES.find(lang => lang.code == language)?.label}
                    </div>
                    <div
                        className={menuItemClassName}
                        onClick={logoutMutation.isPending ? undefined : () => logoutMutation.mutate()}
                    >
                        <LogOut01Icon size={20} />
                        {t('menu:logout')}
                        {logoutMutation.isPending && <Spinner size={20} />}
                    </div>
                </div>
            </div>
            {isLanguageModalOpen && <LangSelectionModal onCloseCb={() => setIsLanguageModalOpen(false)} />}
        </>
    )
}
